import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { PROVENANCE_COPY } from '@/copy/money'
import type { PanelMeta, Provenance } from '@/types/api'

/** Colour is NEVER semantic here — a chip is a caveat, not a status. */
const TONE: Record<Provenance, { border: string; dashed?: boolean }> = {
  LEDGER: { border: 'divider' },
  MEASURED: { border: 'divider' },
  EXTERNAL: { border: 'divider' },
  // MODELED alone gets a distinct visual language, matched by the chart marks:
  // dashed stroke, no solid fill. A screenshot then carries its own epistemology.
  MODELED: { border: 'warning.main', dashed: true },
}

export function ProvenanceChip({ p }: { p: Provenance }) {
  const copy = PROVENANCE_COPY[p]
  const tone = TONE[p]
  return (
    <Tooltip title={copy.help} placement="top">
      <Chip
        label={copy.label}
        size="small"
        variant="outlined"
        sx={{
          height: 18,
          fontSize: '0.65rem',
          color: 'text.secondary',
          borderColor: tone.border,
          borderStyle: tone.dashed ? 'dashed' : 'solid',
        }}
      />
    </Tooltip>
  )
}

/**
 * Pinned to every panel. A screenshot that leaves the room carries WHERE its
 * numbers came from and WHEN — which is the only defence against the screenshot
 * itself, and screenshots are the main output of a staff tool.
 */
export function ProvenanceFooter({ meta }: { meta: PanelMeta }) {
  const when = new Date(meta.generated_at)
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pt: 1,
        borderTop: 1,
        borderColor: 'divider',
        flexWrap: 'wrap',
      }}
    >
      {meta.sources.map((s) => (
        <ProvenanceChip key={s} p={s} />
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', opacity: 0.85 }}>
        {when.toISOString().replace('T', ' ').slice(0, 16)} {meta.tz} · {meta.query_ms} ms
      </Typography>
    </Box>
  )
}

/** Always visible, top right. See the note on ProvenanceFooter. */
export function ProvenanceLegend() {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {(Object.keys(PROVENANCE_COPY) as Provenance[]).map((p) => (
        <ProvenanceChip key={p} p={p} />
      ))}
    </Box>
  )
}
